import { Outlet, Link, useLocation, useNavigate } from "react-router-dom";
import { Users, FileText, LayoutDashboard, ArrowLeft, LogOut, Settings as SettingsIcon, Bell, Calculator, Clock, Calendar, MessageSquare, Save, ChevronDown, ChevronUp, Plus, Package, Wrench, Database, Shield, CreditCard, HardDrive } from "lucide-react";
import { Button } from "@/components/ui/button";
import axios from "axios";
import { toast } from "sonner";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { useEffect, useState } from "react";
import Logo from "@/components/Logo";
import LabelPrinting from "@/components/LabelPrinting";
import ThemeToggle from "@/components/ThemeToggle";
import { useSubscription } from "@/context/SubscriptionContext";
import { AuthCheckSkeleton } from "@/components/AuthCheckSkeleton";

const AdminLayout = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [showNotifications, setShowNotifications] = useState(false);
  const [completedOrders, setCompletedOrders] = useState([]);
  const [notificationCount, setNotificationCount] = useState(0);
  const [expandedWO, setExpandedWO] = useState(null);
  const [commitments, setCommitments] = useState({});
  const [newEntry, setNewEntry] = useState({ date: "", comment: "" });
  const [savingDelivery, setSavingDelivery] = useState(false);
  const [isMaster, setIsMaster] = useState(false);
  // Hide protected admin content until the session check completes — without
  // this gate the admin shell paints for one frame before the redirect to
  // /admin-login fires, which users perceive as "admin page flashes on click".
  const [authChecking, setAuthChecking] = useState(true);
  const { hasFeature, package: currentPackage } = useSubscription();

  const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
  const API = `${BACKEND_URL}/api`;

  // Nav items — package-aware via `feature` key, master-only via `masterOnly`.
  const allNavItems = [
    { path: "/admin/dashboard", label: "Dashboard", icon: LayoutDashboard, feature: "admin_dashboard" },
    { path: "/admin/timesheet", label: "Timesheet", icon: Clock, feature: "admin_timesheet" },
    { path: "/admin/production-tracking", label: "Production", icon: Package, feature: "admin_production" },
    { path: "/admin/cost-calculator", label: "Costing", icon: Calculator, feature: "cost_calculator" },
    { path: "/admin/completion-history", label: "History", icon: Clock, feature: "admin_history" },
    { path: "/admin/reports", label: "Reports", icon: FileText, feature: "admin_reports" },
    { path: "/admin/maintenance", label: "Maintenance", icon: Wrench, feature: "admin_maintenance" },
    { path: "/admin/database-monitor", label: "Database", icon: Database, feature: "admin_database" },
    { path: "/admin/backups", label: "Backups", icon: HardDrive, feature: "admin_database" },
    { path: "/admin/security", label: "Security", icon: Shield, feature: "admin_security", masterOnlyInCore: true },
    { path: "/admin/subscription", label: "Subscription", icon: CreditCard, masterOnly: true },
  ];
  const navItems = allNavItems.filter((item) => {
    if (item.masterOnly && !isMaster) return false;
    // Security is master-only on Core, but admin+master on Pro.
    if (item.masterOnlyInCore && currentPackage === "core" && !isMaster) return false;
    if (item.feature && !hasFeature(item.feature)) return false;
    return true;
  });

  // Fetch completed orders for notifications
  const fetchCompletedOrders = async () => {
    try {
      const response = await axios.get(`${API}/work-orders`);
      const completed = response.data.filter(wo => wo.status === "completed");
      setCompletedOrders(completed);
      setNotificationCount(completed.length);
    } catch (error) {
      console.error("Error fetching completed orders:", error);
    }
  };

  const fetchCommitments = async (woNumber) => {
    try {
      const res = await axios.get(`${API}/work-orders/${woNumber}/delivery-commitments`);
      setCommitments(prev => ({ ...prev, [woNumber]: res.data.commitments || [] }));
    } catch {
      setCommitments(prev => ({ ...prev, [woNumber]: [] }));
    }
  };

  const toggleWOExpand = (woNumber) => {
    if (expandedWO === woNumber) {
      setExpandedWO(null);
    } else {
      setExpandedWO(woNumber);
      setNewEntry({ date: "", comment: "" });
      fetchCommitments(woNumber);
    }
  };

  const saveNewCommitment = async (woNumber) => {
    if (!newEntry.date) {
      toast.error("Please select a delivery date");
      return;
    }
    setSavingDelivery(true);
    try {
      await axios.patch(`${API}/work-orders/${woNumber}/committed-delivery`, {
        committed_delivery_date: newEntry.date,
        delivery_comment: newEntry.comment,
      });
      toast.success("Delivery commitment added");
      setNewEntry({ date: "", comment: "" });
      fetchCommitments(woNumber);
    } catch {
      toast.error("Failed to save");
    } finally {
      setSavingDelivery(false);
    }
  };

  useEffect(() => {
    // Check session from httpOnly cookie
    const verifyAuth = async () => {
      try {
        const { checkSession } = await import("@/utils/auth");
        const session = await checkSession();
        if (!session.authenticated || (!session.roles.includes("admin") && !session.is_master)) {
          toast.error("Please login to access admin panel");
          navigate("/admin-login");
          return;
        }
        setIsMaster(!!session.is_master);
        // Guard: /admin/subscription is always master-only.
        // /admin/security is master-only on Core, but admin+master on Pro.
        if (location.pathname === "/admin/subscription" && !session.is_master) {
          toast.error("This page is accessible to the vendor account only");
          navigate("/admin/dashboard");
          return;
        }
        if (location.pathname === "/admin/security" && currentPackage === "core" && !session.is_master) {
          toast.error("Security settings are managed by the vendor account on the Core plan.");
          navigate("/admin/dashboard");
          return;
        }
        // Auth & route-guards passed — release the render gate.
        setAuthChecking(false);
      } catch {
        toast.error("Please login to access admin panel");
        navigate("/admin-login");
        return;
      }
      fetchCompletedOrders();
    };
    verifyAuth();

    // Real-time updates - refresh every 30 seconds
    const interval = setInterval(fetchCompletedOrders, 30000);
    return () => clearInterval(interval);
  }, [navigate, location.pathname]);

  const handleLogout = async () => {
    try { const { logout } = await import("@/utils/auth"); await logout(); } catch { /* ignore */ }
    toast.success("Logged out successfully");
    navigate("/");
  };

  // Render-time gate: nothing renders until auth + route guards resolve.
  if (authChecking) return <AuthCheckSkeleton />;

  return (
    <div className="min-h-screen admin-theme flex flex-col">
      {/* Navigation */}
      <nav className="bg-black/90 shadow-2xl border-b-2 border-slate-600/50 backdrop-blur-sm relative">
        <div className="absolute inset-0 bg-gradient-to-r from-transparent via-slate-500/5 to-transparent"></div>
        <div className="max-w-[1600px] mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="flex justify-between items-center h-14 gap-4">
            <div className="flex items-center gap-6 min-w-0">
              {/* Logo - clickable to dashboard */}
              <div className="cursor-pointer shrink-0" onClick={() => navigate("/admin/dashboard")}>
                <Logo size="md" showText={false} />
              </div>

              {/* Nav Items */}
              <div className="flex items-center gap-1 overflow-x-auto scrollbar-hide">
                {navItems.map((item) => {
                  const Icon = item.icon;
                  const isActive = location.pathname === item.path ||
                    (location.pathname === "/admin" && item.path === "/admin/dashboard");
                  return (
                    <Link
                      key={item.path}
                      to={item.path}
                      data-testid={`nav-${item.label.toLowerCase()}`}
                      className={`inline-flex items-center whitespace-nowrap px-3 py-1.5 rounded-md text-xs font-semibold tracking-wide transition-all ${
                        isActive
                          ? "bg-cyan-500/15 text-cyan-400 ring-1 ring-cyan-400/50"
                          : "text-gray-400 hover:text-cyan-300 hover:bg-white/5"
                      }`}
                    >
                      <Icon className="h-3.5 w-3.5 mr-1.5 shrink-0" />
                      {item.label}
                    </Link>
                  );
                })}
              </div>
            </div>
            <div className="flex items-center gap-1.5 shrink-0">
              {/* Label Printing Button */}
              <LabelPrinting />

              {/* Theme Toggle */}
              <ThemeToggle />

              {/* Notification Bell Icon */}
              <button
                onClick={() => setShowNotifications(true)}
                className="relative p-2 text-cyan-400 hover:text-cyan-300 hover:bg-white/5 rounded-md transition-all"
                data-testid="notifications-btn"
              >
                <Bell className="h-5 w-5" />
                {notificationCount > 0 && (
                  <span className="absolute -top-0.5 -right-0.5 bg-red-500 text-white text-[10px] font-bold rounded-full h-4 w-4 flex items-center justify-center animate-pulse">
                    {notificationCount}
                  </span>
                )}
              </button>

              <Button
                data-testid="logout-button"
                onClick={handleLogout}
                variant="outline"
                size="sm"
                className="h-8 border-slate-500/50 text-cyan-400 hover:bg-cyan-500 hover:text-black font-semibold text-xs"
              >
                <LogOut className="h-3.5 w-3.5 mr-1" />
                Logout
              </Button>
              <Link to="/">
                <Button variant="ghost" size="sm" className="h-8 text-cyan-400 hover:bg-white/5 text-xs" data-testid="home-btn">
                  <ArrowLeft className="h-3.5 w-3.5 mr-1" />
                  Home
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </nav>

      {/* Content */}
      <main className="flex-1 overflow-y-auto">
        <Outlet />
      </main>

      {/* Notification Modal */}
      <Dialog open={showNotifications} onOpenChange={setShowNotifications}>
        <DialogContent className="sm:max-w-[600px] bg-black border-2 border-slate-600">
          <DialogHeader>
            <DialogTitle className="text-2xl text-cyan-400 flex items-center gap-2">
              <Bell className="h-6 w-6" />
              Completed Orders Ready for Delivery
            </DialogTitle>
            <DialogDescription className="text-gray-400">
              {notificationCount} work order(s) completed and ready for delivery
            </DialogDescription>
          </DialogHeader>
          <div className="mt-4 space-y-3 max-h-[400px] overflow-y-auto">
            {completedOrders.length === 0 ? (
              <div className="text-center py-8 text-gray-500">
                No completed orders at the moment
              </div>
            ) : (
              completedOrders.map((order) => {
                const woNum = order.header?.work_order_number;
                const isExpanded = expandedWO === woNum;
                const woCommitments = commitments[woNum] || [];
                
                return (
                <div
                  key={order.id}
                  className="bg-green-500/10 border border-green-500/30 rounded-lg overflow-hidden"
                >
                  <div className="p-4">
                    <div className="flex justify-between items-start">
                      <div className="flex-1">
                        <h3 className="text-lg font-semibold text-white">
                          {woNum}
                        </h3>
                        <p className="text-sm text-gray-400">
                          Customer: {order.header?.customer_name}
                        </p>
                        <p className="text-sm text-gray-400">
                          Item: {order.header?.item_name} | Qty: {order.header?.required_quantity}
                        </p>
                      </div>
                      <div className="flex items-center gap-2">
                        <span className="inline-block px-3 py-1 bg-green-600 text-white text-xs font-bold rounded-full">
                          COMPLETED
                        </span>
                        <button
                          onClick={() => toggleWOExpand(woNum)}
                          className="p-1.5 rounded hover:bg-white/10 transition-colors text-cyan-400"
                          data-testid={`delivery-dropdown-${woNum}`}
                        >
                          {isExpanded ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
                        </button>
                      </div>
                    </div>
                  </div>
                  
                  {isExpanded && (
                    <div className="border-t border-green-500/20 p-4 space-y-3 bg-black/30">
                      {/* Add New Entry */}
                      <div className="p-3 border border-cyan-500/30 rounded-lg bg-cyan-500/5 space-y-2">
                        <p className="text-xs font-semibold text-cyan-400 flex items-center gap-1">
                          <Plus className="h-3 w-3" /> Add New Committed Delivery
                        </p>
                        <div className="flex items-end gap-2">
                          <div className="flex-1">
                            <label className="text-xs text-gray-400 mb-1 block">Date</label>
                            <input
                              type="date"
                              value={newEntry.date}
                              onChange={(e) => setNewEntry(prev => ({ ...prev, date: e.target.value }))}
                              className="w-full px-2 py-1.5 bg-black/50 border border-gray-600 rounded text-white text-sm focus:border-cyan-400 focus:outline-none"
                              data-testid={`new-delivery-date-${woNum}`}
                            />
                          </div>
                          <div className="flex-1">
                            <label className="text-xs text-gray-400 mb-1 block">Comment</label>
                            <input
                              type="text"
                              value={newEntry.comment}
                              onChange={(e) => setNewEntry(prev => ({ ...prev, comment: e.target.value }))}
                              placeholder="Delivery notes..."
                              className="w-full px-2 py-1.5 bg-black/50 border border-gray-600 rounded text-white text-sm focus:border-cyan-400 focus:outline-none"
                              data-testid={`new-delivery-comment-${woNum}`}
                            />
                          </div>
                          <Button
                            size="sm"
                            onClick={() => saveNewCommitment(woNum)}
                            disabled={savingDelivery}
                            className="bg-cyan-600 hover:bg-cyan-700 text-white h-8 px-3"
                            data-testid={`save-commitment-${woNum}`}
                          >
                            <Save className="h-3 w-3 mr-1" />
                            Add
                          </Button>
                        </div>
                      </div>

                      {/* Commitments Table */}
                      {woCommitments.length > 0 ? (
                        <div className="rounded-lg overflow-hidden border border-gray-700">
                          <table className="w-full text-sm">
                            <thead>
                              <tr className="bg-gray-800/80 text-gray-400">
                                <th className="text-left px-3 py-2 text-xs font-semibold">#</th>
                                <th className="text-left px-3 py-2 text-xs font-semibold">Committed Date</th>
                                <th className="text-left px-3 py-2 text-xs font-semibold">Comment</th>
                                <th className="text-left px-3 py-2 text-xs font-semibold">Added On</th>
                              </tr>
                            </thead>
                            <tbody>
                              {woCommitments.slice().reverse().map((c, idx) => (
                                <tr key={idx} className="border-t border-gray-700/50 hover:bg-gray-800/30">
                                  <td className="px-3 py-2 text-gray-500">{woCommitments.length - idx}</td>
                                  <td className="px-3 py-2 text-cyan-400 font-medium">
                                    {c.date ? new Date(c.date).toLocaleDateString() : "-"}
                                  </td>
                                  <td className="px-3 py-2 text-gray-300">{c.comment || "-"}</td>
                                  <td className="px-3 py-2 text-gray-500 text-xs">
                                    {c.created_at ? new Date(c.created_at).toLocaleDateString() : "-"}
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      ) : (
                        <p className="text-xs text-gray-500 text-center py-2">No delivery commitments yet</p>
                      )}
                    </div>
                  )}
                </div>
                );
              })
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminLayout;